import React, { useState } from 'react';

export default function Step1Personal({ formData, errors, updateField, clearError, setError }) {
  const lockedEmail = sessionStorage.getItem('instructorPreEmail') || '';

  // DOB - split into DD/MM/YYYY text inputs for cross-browser mobile compatibility
  const initDob = () => {
    const v = formData.date_of_birth || '';
    const [y, m, d] = v.split('-');
    return { day: d || '', month: m || '', year: y || '' };
  };
  const [dobParts, setDobParts] = useState(initDob);

  const handleDob = (part, rawVal) => {
    const maxLen = part === 'year' ? 4 : 2;
    const val = rawVal.replace(/\D/g, '').slice(0, maxLen);
    const next = { ...dobParts, [part]: val };
    setDobParts(next);
    if (next.day && next.month && next.year.length === 4) {
      updateField('date_of_birth', `${next.year}-${next.month.padStart(2, '0')}-${next.day.padStart(2, '0')}`);
    } else {
      updateField('date_of_birth', '');
    }
    clearError('date_of_birth');
  };

  const h = (e) => {
    const { name, value } = e.target;
    updateField(name, value);
    clearError(name);
  };

  const hUpper = (name, value) => {
    updateField(name, value.toUpperCase());
    clearError(name);
  };

  const handleFullNameBlur = () => {
    const nm = (formData.full_name || '').trim();
    if (!nm) return; // required check handled on submit
    if (nm.length < 2) { setError('full_name', 'Full name is too short — please enter your full name'); return; }
    if (nm.length > 80) { setError('full_name', 'Full name must be 80 characters or fewer'); return; }
    if (/\d/.test(nm)) { setError('full_name', 'Full name must not contain numbers'); return; }
    if (/[^A-Za-z\s\u002D\u0027\u2019\u00C0-\u017F]/.test(nm)) {
      setError('full_name', 'Full name may only contain letters, spaces, hyphens, and apostrophes');
      return;
    }
    clearError('full_name');
  };

  return (
    <div className="adi-step-content">
      <div className="adi-step-header">
        <span className="adi-step-num-badge">1</span>
        <div>
          <h2 className="adi-step-title">Personal Details</h2>
          <p className="adi-step-desc">Your personal and contact information</p>
        </div>
      </div>

      <div className="adi-field-grid">

        <div className="adi-field adi-field--full">
          <label>Full Name (as on ADI licence) *</label>
          <input name="full_name" value={formData.full_name || ''} onChange={h}
            onBlur={handleFullNameBlur}
            placeholder="e.g. John Michael Smith"
            maxLength={80}
            className={errors.full_name ? 'err' : ''} />
          {errors.full_name && <span className="adi-err">{errors.full_name}</span>}
        </div>

        <div className="adi-field">
          <label>Date of Birth * <em style={{ fontWeight: 400, fontSize: '0.8rem' }}>(DD / MM / YYYY)</em></label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.8fr', gap: '0.5rem' }}>
            <input
              type="text"
              inputMode="numeric"
              placeholder="DD"
              value={dobParts.day}
              onChange={e => handleDob('day', e.target.value)}
              className={errors.date_of_birth ? 'err' : ''}
              maxLength={2}
            />
            <input
              type="text"
              inputMode="numeric"
              placeholder="MM"
              value={dobParts.month}
              onChange={e => handleDob('month', e.target.value)}
              className={errors.date_of_birth ? 'err' : ''}
              maxLength={2}
            />
            <input
              type="text"
              inputMode="numeric"
              placeholder="YYYY"
              value={dobParts.year}
              onChange={e => handleDob('year', e.target.value)}
              className={errors.date_of_birth ? 'err' : ''}
              maxLength={4}
            />
          </div>
          <p className="adi-field-note">Must be at least 18 years old · e.g. 15 08 1985</p>
          {errors.date_of_birth && <span className="adi-err">{errors.date_of_birth}</span>}
        </div>

        <div className="adi-field">
          <label>National Insurance Number <em>(optional)</em></label>
          <input name="ni_number" value={formData.ni_number || ''}
            onChange={e => hUpper(e.target.name, e.target.value)}
            placeholder="e.g. AB 12 34 56 C"
            style={{ textTransform: 'uppercase' }}
            className={errors.ni_number ? 'err' : ''} 
            maxLength={9}/>
          {errors.ni_number
            ? <span className="adi-err">{errors.ni_number}</span>
            : <p className="adi-field-note">Format: AB 12 34 56 C — 2 letters · 6 digits · letter A–D</p>
          }
        </div>

        <div className="adi-field">
          <label>Phone Number *</label>
          <input type="tel" name="phone" value={formData.phone || ''} onChange={h}
            placeholder="07xxx xxxxxx"
            className={errors.phone ? 'err' : ''} 
            maxLength={11}/>
          {errors.phone
            ? <span className="adi-err">{errors.phone}</span>
            : <p className="adi-field-note">UK mobile or landline (e.g. 07911 123456 or 020 1234 5678)</p>
          }
        </div>

        <div className="adi-field">
          <label>Email Address *</label>
          <input type="email" name="email" value={formData.email || ''}
            onChange={lockedEmail ? undefined : h}
            readOnly={!!lockedEmail}
            disabled={!!lockedEmail}
            placeholder="you@example.com"
            className={errors.email ? 'err' : ''}
            style={lockedEmail ? { background: '#f1f5f9', color: '#475569', cursor: 'not-allowed' } : {}}
          />
          {errors.email
            ? <span className="adi-err">{errors.email}</span>
            : lockedEmail
              ? <p className="adi-field-note" style={{ color: '#16a34a', fontWeight: 600 }}>&#10003; Verified email from your account — cannot be changed</p>
              : null
          }
        </div>

        <div className="adi-field adi-field--full">
          <label>Address Line 1 *</label>
          <input name="address_line1" value={formData.address_line1 || ''} onChange={h}
            placeholder="House number and street name"
            className={errors.address_line1 ? 'err' : ''} />
          {errors.address_line1 && <span className="adi-err">{errors.address_line1}</span>}
        </div>

        <div className="adi-field adi-field--full">
          <label>Address Line 2 <em>(optional)</em></label>
          <input name="address_line2" value={formData.address_line2 || ''} onChange={h}
            placeholder="Flat / apartment number" />
        </div>

        <div className="adi-field">
          <label>City / Town *</label>
          <input name="city" value={formData.city || ''} onChange={h} placeholder="City"
            className={errors.city ? 'err' : ''} />
          {errors.city && <span className="adi-err">{errors.city}</span>}
        </div>

        <div className="adi-field">
          <label>Postcode *</label>
          <input name="postcode" value={formData.postcode || ''}
            onChange={e => hUpper(e.target.name, e.target.value)}
            placeholder="e.g. SW1A 1AA"
            style={{ textTransform: 'uppercase' }}
            maxLength={8}
            className={errors.postcode ? 'err' : ''} />
          {errors.postcode
            ? <span className="adi-err">{errors.postcode}</span>
            : <p className="adi-field-note">UK postcode — auto-uppercased</p>
          }
        </div>

      </div>
    </div>
  );
}
